import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

export interface MainroomAttendanceCode {
    id: number;
    period_id: number;
    code: string;
    label: string | null;
    is_active: boolean;
    created_by: number | null;
    created_at?: string;
    updated_at?: string;
    creator?: { user_id: number; name: string } | null;
}

export interface MainroomAttendance {
    id: number;
    period_id: number;
    category: "vip" | "registered" | "other_invitee";
    registration_id: number | null;
    full_name: string;
    email: string;
    institution: string | null;
    is_presenter: boolean;
    eligible_for_certificate: boolean;
    attendance_code_id: number | null;
    ip_address: string | null;
    attended_at: string | null;
    created_at?: string;
    updated_at?: string;
    attendance_code?: MainroomAttendanceCode | null;
}

export const useAdminMainroomAttendances = (params?: { period_id?: string; category?: string; search?: string; attendance_code_id?: string }) => {
    return useQuery({
        queryKey: ["admin-mainroom-attendances", params],
        queryFn: async () => {
            const { data } = await axiosClient.get("/private/mainroom-attendances", { params });
            return data.data as MainroomAttendance[];
        },
    });
};

export const exportMainroomAttendancesExcel = async (params?: {
    period_id?: string;
    category?: string;
    attendance_code_id?: string;
    search?: string;
}) => {
    const response = await axiosClient.get("/private/mainroom-attendances/export/excel", {
        params,
        responseType: "blob",
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;

    const contentDisposition = response.headers["content-disposition"];
    let fileName = "Presensi_Mainroom.xlsx";
    if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length === 2) {
            fileName = fileNameMatch[1];
        }
    }

    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();

    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
};

export const useDeleteMainroomAttendance = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`/private/mainroom-attendances/${id}`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-mainroom-attendances"] });
        },
    });
};

export const useAdminMainroomAttendanceCodes = (periodId?: string) => {
    return useQuery({
        queryKey: ["admin-mainroom-attendance-codes", periodId],
        queryFn: async () => {
            const { data } = await axiosClient.get("/private/mainroom-attendance-codes", {
                params: periodId ? { period_id: periodId } : {},
            });
            return data.data as MainroomAttendanceCode[];
        },
    });
};

export const useCreateMainroomAttendanceCode = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: { period_id: number; code: string; label?: string }) => {
            const { data } = await axiosClient.post("/private/mainroom-attendance-codes", payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-mainroom-attendance-codes"] });
        },
    });
};

export const useDeactivateMainroomAttendanceCode = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.post(`/private/mainroom-attendance-codes/${id}/deactivate`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-mainroom-attendance-codes"] });
        },
    });
};
